import * as c from "@yahshi/contracts";
import { createAdminRequest, type AdminHttpOptions } from "../../../lib/adminHttp";

export interface AiModelApi {
  listProviders(): Promise<c.AdminAiProvider[]>;
  createProvider(input: c.AdminAiProviderCreateInput): Promise<c.AdminAiProvider>;
  updateProvider(id: string, input: c.AdminAiProviderUpdateInput): Promise<c.AdminAiProvider>;
  listModels(): Promise<c.AdminAiModel[]>;
  createModel(input: c.AdminAiModelCreateInput): Promise<c.AdminAiModel>;
  updateModel(id: string, input: c.AdminAiModelUpdateInput): Promise<c.AdminAiModel>;
  publishModel(id: string, input: { expectedVersion: number }): Promise<c.AdminAiModel>;
  listGrants(): Promise<c.AdminAiGrant[]>;
  upsertGrant(input: c.AdminAiGrantUpsertInput): Promise<c.AdminAiGrant>;
  listUsage(query?: Partial<c.AdminAiUsageQuery>): Promise<c.AdminAiUsage[]>;
  invoke(input: c.AdminAiInvokeInput): Promise<c.AiInvokeResult>;
}

const qs = (value: object) => new URLSearchParams(Object.entries(value).map(([key, item]) => [key, String(item)])).toString();

export function createHttpAiModelApi(options: AdminHttpOptions): AiModelApi {
  const request = createAdminRequest(options);
  return {
    listProviders: () => request("/admin/ai/providers", c.adminAiProviderSchema.array()),
    createProvider: (input) => request("/admin/ai/providers", c.adminAiProviderSchema, {
      method: "POST",
      body: JSON.stringify(c.adminAiProviderCreateSchema.parse(input)),
    }),
    updateProvider: (id, input) => request(`/admin/ai/providers/${encodeURIComponent(id)}`, c.adminAiProviderSchema, {
      method: "PATCH",
      body: JSON.stringify(c.adminAiProviderUpdateSchema.parse(input)),
    }),
    listModels: () => request("/admin/ai/models", c.adminAiModelSchema.array()),
    createModel: (input) => request("/admin/ai/models", c.adminAiModelSchema, {
      method: "POST",
      body: JSON.stringify(c.adminAiModelCreateSchema.parse(input)),
    }),
    updateModel: (id, input) => request(`/admin/ai/models/${encodeURIComponent(id)}`, c.adminAiModelSchema, {
      method: "PATCH",
      body: JSON.stringify(c.adminAiModelUpdateSchema.parse(input)),
    }),
    publishModel: (id, input) => request(`/admin/ai/models/${encodeURIComponent(id)}/publish`, c.adminAiModelSchema, {
      method: "POST",
      body: JSON.stringify(c.adminAiModelPublishSchema.parse(input)),
    }),
    listGrants: () => request("/admin/ai/grants", c.adminAiGrantSchema.array()),
    upsertGrant: (input) => request("/admin/ai/grants", c.adminAiGrantSchema, {
      method: "PUT",
      body: JSON.stringify(c.adminAiGrantUpsertSchema.parse(input)),
    }),
    listUsage: (query = {}) => request(`/admin/ai/usage?${qs(c.adminAiUsageQuerySchema.parse(query))}`, c.adminAiUsageSchema.array()),
    invoke: (input) => request("/admin/ai/invoke", c.aiInvokeResultSchema, {
      method: "POST",
      body: JSON.stringify(c.adminAiInvokeInputSchema.parse(input)),
    }),
  };
}
